import { type ComponentProps, useCallback, useMemo } from 'react';

import { useSelector } from '@xstate/react';
import { createActor, type SnapshotFrom } from 'xstate';

import { useIsFocused } from '@sg/garnish';

import { useGlobalContent } from '../../../hooks/use-global-content';
import { FLOATING_NOTICE_PALETTES, type FloatingNotice } from '../FloatingNotice';
import { floatingNoticeMachine } from '../machine';

// ─────────────────────────────────────────────────────────────────────────────

export const useFloatingNotice = () => {
  const isScreenFocused = useIsFocused();
  const { fields } = useGlobalContent({ pause: !isScreenFocused });
  const dismissedNoticeText = useSelector(floatingNoticeActor, selectDismissedNoticeText);

  // ─── Derived ───────────────────────────────────────────────────────────────

  const noticeText = fields.noticeText?.trim() || undefined;
  const noticePalette = resolveNoticePalette(fields.noticePalette);

  const notice = useMemo(() => {
    if (!noticeText) return;
    if (noticeText === dismissedNoticeText) return;

    return { text: noticeText, palette: noticePalette };
  }, [dismissedNoticeText, noticePalette, noticeText]);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const dismissNotice = useCallback(() => {
    if (!noticeText) return;

    floatingNoticeActor.send({ type: 'DISMISS', noticeText });
  }, [noticeText]);

  // ───────────────────────────────────────────────────────────────────────────

  return { notice, dismissNotice };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function resolveNoticePalette(value: string | undefined): FloatingNoticePalette {
  if (!checkIfFloatingNoticePalette(value)) return DEFAULT_NOTICE_PALETTE;

  return value;
}

function checkIfFloatingNoticePalette(value: string | undefined): value is FloatingNoticePalette {
  return FLOATING_NOTICE_PALETTES.some((palette) => palette === value);
}

function selectDismissedNoticeText(snapshot: FloatingNoticeSnapshot) {
  return snapshot.context.dismissedNoticeText;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const DEFAULT_NOTICE_PALETTE: FloatingNoticePalette = 'info';

// Module scoped so a dismissal holds across screens until the next app launch.
const floatingNoticeActor = createActor(floatingNoticeMachine).start();

// ─── Types ───────────────────────────────────────────────────────────────────

type FloatingNoticePalette = NonNullable<ComponentProps<typeof FloatingNotice>['palette']>;

type FloatingNoticeSnapshot = SnapshotFrom<typeof floatingNoticeMachine>;
